import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDebounce } from "use-debounce";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import { searchArtists } from "../../api/spotify";
import client, { apiError } from "../../api/client";
import "./Quiz.scss";

const SUGGESTIONS = [
  "Radiohead",
  "Adele",
  "Kendrick Lamar",
  "Bon Iver",
  "Frank Ocean",
  "Fleetwood Mac",
  "Daft Punk",
  "Hozier",
];

export default function Quiz() {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedInput] = useDebounce(searchInput, 120);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");

  const cache = useRef(new Map()); // query -> results, for instant repeats
  const abortRef = useRef(null);

  // Warm the serverless function on arrival so the first search isn't cold.
  useEffect(() => {
    client.get("/").catch(() => {});
  }, []);

  useEffect(() => {
    const raw = debouncedInput.trim();
    const key = raw.toLowerCase();
    if (!raw) {
      abortRef.current?.abort();
      setResults([]);
      setStatus("idle");
      return;
    }

    // Instant: serve from cache with no network round-trip.
    if (cache.current.has(key)) {
      abortRef.current?.abort();
      setResults(cache.current.get(key));
      setStatus("done");
      return;
    }

    // Cancel any in-flight request so responses can't arrive out of order.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus("loading");
    setError("");
    searchArtists(raw, controller.signal)
      .then((data) => {
        cache.current.set(key, data);
        setResults(data);
        setStatus("done");
      })
      .catch((err) => {
        // Ignore cancellations (a newer keystroke superseded this one).
        if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return;
        setError(apiError(err, "Search failed"));
        setStatus("error");
      });
  }, [debouncedInput]);

  return (
    <div className="quiz">
      <Header />
      <main className="quiz__main">
        <header className="quiz__intro">
          <p className="quiz__eyebrow rise-1">
            <span>01</span> <i /> Find your gateway
          </p>
          <h1 className="quiz__title rise-2">
            Search for an artist
            <span className="quiz__title-em">whose music you already vibe with</span>
          </h1>
        </header>

        <div className="quiz__search rise-3">
          <svg className="quiz__search-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <label htmlFor="artist-search" className="sr-only">Search for an artist</label>
          <input
            id="artist-search"
            type="text"
            className="quiz__input"
            placeholder="e.g. Radiohead, Adele, Kendrick Lamar…"
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button className="quiz__clear" onClick={() => setSearchInput("")} aria-label="Clear search">
              ×
            </button>
          )}
        </div>

        <section className="quiz__results" aria-live="polite">
          {status === "idle" && (
            <div className="quiz__suggest rise-4">
              <p className="quiz__suggest-label">Popular starting points</p>
              <div className="quiz__chips">
                {SUGGESTIONS.map((name) => (
                  <button
                    key={name}
                    className="quiz__chip"
                    onClick={() => setSearchInput(name)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Skeleton only on the very first search (nothing to keep on screen) */}
          {status === "loading" && results.length === 0 && (
            <div className="quiz__grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <div className="quiz__skeleton" key={i}>
                  <span className="skeleton quiz__skeleton-img" />
                  <span className="skeleton quiz__skeleton-text" />
                </div>
              ))}
            </div>
          )}

          {status === "error" && results.length === 0 && (
            <p className="quiz__message">{error}</p>
          )}

          {status === "done" && results.length === 0 && (
            <p className="quiz__message">
              No artists found for “{debouncedInput}”. Try another name.
            </p>
          )}

          {/* Results persist while the next query loads (no skeleton flash) */}
          {results.length > 0 && (
            <div
              className={`quiz__grid${status === "loading" ? " quiz__grid--loading" : ""}`}
            >
              {results.map((artist, i) => (
                <Link
                  to={`/quiz/results/${encodeURIComponent(artist.name)}/${artist.id}`}
                  className="artist"
                  key={artist.id}
                  style={{ "--i": i }}
                >
                  <div className="artist__frame">
                    {artist.image ? (
                      <img src={artist.image} alt={artist.name} className="artist__image" loading="lazy" />
                    ) : (
                      <span className="artist__placeholder">{artist.name[0]}</span>
                    )}
                    <span className="artist__go" aria-hidden="true">→</span>
                  </div>
                  <span className="artist__name">{artist.name}</span>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
